import { motion } from 'framer-motion'
import { useEffect, useMemo, useState } from 'react'

import { GameBackdrop } from '@/features/stadium/GameBackdrop'
import type { RGNEvent } from '@/lib/api/schemas'

/**
 * CHECK_IN phase — two-column "X checked in / Y awaiting" grid.
 *
 * Left column = checked-in entrants (green border, avatar lit).
 * Right column = awaiting (dim border, avatar half-opacity).
 *
 * Avatars are keyed on userId; when a user flips from awaiting →
 * checked-in (poll detects the new `checkedInAt`), Framer Motion's
 * shared `layoutId` slides them across the gap. Per the Emil
 * framework: occasional-frequency moment (10-30 firings during a
 * 30-minute window), use restraint — no spring physics, single
 * 320 ms ease-out slide.
 *
 * Time remaining: tick locally every second so the remaining-minutes
 * pill stays smooth even between 5 s poll cycles.
 */
export function CheckInPhase({ event }: { event: RGNEvent }) {
  const remaining = useRemaining(toMs(event.checkInClosesAt))
  const { checkedIn, awaiting } = useMemo(() => splitAttendees(event), [event])

  const total = checkedIn.length + awaiting.length

  return (
    <div className="relative flex h-full flex-col gap-[clamp(20px,3vh,48px)] px-[clamp(20px,3vw,60px)] py-[clamp(20px,2.5vh,40px)]">
      <GameBackdrop event={event} />
      <div className="relative flex items-end justify-between gap-6">
        <div>
          <p
            className="font-display font-extrabold uppercase"
            style={{
              color: 'var(--tv-primary)',
              letterSpacing: '0.3em',
              fontSize: 'clamp(0.85rem, 1.2vw, 1.2rem)',
            }}
          >
            Check-in
          </p>
          <h2
            className="font-display font-black leading-none tabular-nums"
            style={{ fontSize: 'clamp(3.5rem, 7vw, 7rem)', marginTop: '0.1em' }}
          >
            <span style={{ color: 'var(--tv-live)' }}>{checkedIn.length}</span>
            <span style={{ color: 'var(--tv-fg-subtle)' }}> / {total}</span>
          </h2>
          <p
            className="font-display font-medium uppercase"
            style={{
              color: 'var(--tv-fg-muted)',
              letterSpacing: '0.22em',
              fontSize: 'clamp(0.7rem, 0.9vw, 0.95rem)',
              marginTop: '0.5em',
            }}
          >
            Entrants confirmed
          </p>
        </div>

        <RemainingPill remaining={remaining} />
      </div>

      <div className="relative grid min-h-0 flex-1 grid-cols-2 gap-[clamp(20px,2vw,40px)]">
        <Column
          title="Checked in"
          accent="var(--tv-live)"
          empty="No one yet"
          entrants={checkedIn}
          state="in"
        />
        <Column
          title="Awaiting"
          accent="var(--tv-fg-subtle)"
          empty="All clear"
          entrants={awaiting}
          state="out"
        />
      </div>
    </div>
  )
}

interface EntrantRow {
  userId: string
  displayName: string
  avatarUrl: string | null
  checkedInAt: number | null
}

function Column({
  title,
  accent,
  empty,
  entrants,
  state,
}: {
  title: string
  accent: string
  empty: string
  entrants: EntrantRow[]
  state: 'in' | 'out'
}) {
  return (
    <div className="flex min-h-0 flex-col gap-[clamp(10px,1vh,20px)]">
      <div className="flex items-baseline justify-between">
        <span
          className="font-display font-extrabold uppercase"
          style={{
            color: accent,
            letterSpacing: '0.25em',
            fontSize: 'clamp(0.75rem, 0.95vw, 1rem)',
          }}
        >
          {title}
        </span>
        <span
          className="font-display font-extrabold tabular-nums"
          style={{ color: accent, fontSize: 'clamp(1.25rem, 1.6vw, 1.8rem)' }}
        >
          {entrants.length}
        </span>
      </div>

      {entrants.length === 0 ? (
        <div
          className="flex flex-1 items-center justify-center rounded-2xl border border-dashed"
          style={{
            borderColor: 'var(--tv-border)',
            backgroundColor: 'var(--tv-bg-elev)',
            color: 'var(--tv-fg-subtle)',
            fontSize: 'clamp(0.95rem, 1.1vw, 1.2rem)',
          }}
        >
          {empty}
        </div>
      ) : (
        <div className="grid min-h-0 flex-1 auto-rows-min grid-cols-[repeat(auto-fill,minmax(clamp(140px,12vw,180px),1fr))] content-start gap-[clamp(8px,1vw,16px)] overflow-hidden">
          {entrants.map((e) => (
            <motion.div
              key={e.userId}
              layoutId={`entrant-${e.userId}`}
              layout
              transition={{ duration: 0.32, ease: [0.23, 1, 0.32, 1] }}
            >
              <EntrantTile entrant={e} state={state} />
            </motion.div>
          ))}
        </div>
      )}
    </div>
  )
}

function EntrantTile({ entrant, state }: { entrant: EntrantRow; state: 'in' | 'out' }) {
  const initials = (entrant.displayName || '?').slice(0, 2).toUpperCase()
  return (
    <div
      className="flex items-center gap-[clamp(8px,0.8vw,12px)] rounded-xl border px-[clamp(8px,0.8vw,14px)] py-[clamp(6px,0.6vw,10px)]"
      style={{
        borderColor: state === 'in' ? 'var(--tv-live)' : 'var(--tv-border)',
        backgroundColor: state === 'in' ? 'rgba(0,255,136,0.06)' : 'var(--tv-bg-elev)',
        opacity: state === 'in' ? 1 : 0.65,
      }}
    >
      {entrant.avatarUrl ? (
        <img
          src={entrant.avatarUrl}
          alt=""
          className="shrink-0 rounded-full object-cover"
          style={{ width: 'clamp(36px,3vw,120px)', height: 'clamp(36px,3vw,120px)' }}
          loading="lazy"
        />
      ) : (
        <div
          className="font-display flex shrink-0 items-center justify-center rounded-full font-black"
          style={{
            width: 'clamp(36px,3vw,120px)',
            height: 'clamp(36px,3vw,120px)',
            backgroundColor: 'var(--tv-bg-surface)',
            color: state === 'in' ? 'var(--tv-live)' : 'var(--tv-fg-muted)',
            fontSize: 'clamp(0.85rem,1vw,1.05rem)',
          }}
        >
          {initials}
        </div>
      )}
      <span
        className="font-display truncate font-semibold"
        style={{
          color: 'var(--tv-fg)',
          fontSize: 'clamp(0.85rem,1vw,1.05rem)',
        }}
      >
        {entrant.displayName || 'Player'}
      </span>
    </div>
  )
}

function RemainingPill({ remaining }: { remaining: number | null }) {
  if (remaining == null) return null
  const expired = remaining <= 0
  const minutes = Math.max(0, Math.floor(remaining / 60_000))
  const seconds = Math.max(0, Math.floor((remaining / 1000) % 60))
  const label = expired
    ? 'Closed'
    : minutes >= 1
      ? `${minutes}m left`
      : `${seconds}s left`
  const accent = expired ? 'var(--tv-fg-subtle)' : minutes < 5 ? 'var(--tv-gold)' : 'var(--tv-primary)'
  return (
    <span
      className="font-display rounded-full border px-[clamp(14px,1.2vw,22px)] py-[clamp(6px,0.7vh,12px)] font-extrabold tabular-nums uppercase"
      style={{
        borderColor: accent,
        color: accent,
        backgroundColor: `color-mix(in oklab, ${accent} 8%, transparent)`,
        letterSpacing: '0.2em',
        fontSize: 'clamp(0.85rem, 1.1vw, 1.2rem)',
      }}
    >
      {label}
    </span>
  )
}

function useRemaining(targetMs: number | null): number | null {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [])
  if (targetMs == null) return null
  return targetMs - now
}

function splitAttendees(event: RGNEvent): {
  checkedIn: EntrantRow[]
  awaiting: EntrantRow[]
} {
  const checkedIn: EntrantRow[] = []
  const awaiting: EntrantRow[] = []
  // `event.attendees` is .passthrough()'d so checkedInAt is typed unknown.
  // Narrow via a tolerant accessor — both ISO strings and epoch-ms are in
  // play depending on which serialiser wrote the row.
  for (const a of event.attendees ?? []) {
    const checkedInRaw = (a as { checkedInAt?: string | number | null }).checkedInAt
    const row: EntrantRow = {
      userId: a.userId,
      displayName: a.displayName ?? 'Player',
      avatarUrl: a.avatarUrl ?? null,
      checkedInAt: toMs(checkedInRaw ?? null),
    }
    if (row.checkedInAt != null) checkedIn.push(row)
    else awaiting.push(row)
  }
  // Sort: checked-in by most-recent first (so the just-arrived player
  // sits at the top-left and gets noticed). Awaiting by displayName
  // for deterministic order.
  checkedIn.sort((a, b) => (b.checkedInAt ?? 0) - (a.checkedInAt ?? 0))
  awaiting.sort((a, b) => a.displayName.localeCompare(b.displayName))
  return { checkedIn, awaiting }
}

function toMs(v: number | string | null | undefined): number | null {
  if (v == null) return null
  if (typeof v === 'number') return v
  const parsed = Date.parse(v)
  return Number.isFinite(parsed) ? parsed : null
}
